/**
 * Delivery Zone Checker
 * Powers sections/delivery-zone-checker.liquid
 *
 * Reads zone rules from the DOM (each zone card carries its postal-code ranges
 * and its cutoff -> delivery rules), computes the next real delivery date for
 * each zone, and personalises the answer when a visitor types a postal code.
 *
 * No data leaves the page: matching is done client-side on the 4-digit prefix.
 */
(function () {
  var WEEKDAYS = [
    "domingo",
    "segunda-feira",
    "terça-feira",
    "quarta-feira",
    "quinta-feira",
    "sexta-feira",
    "sábado",
  ];

  var MINUTE = 60 * 1000;
  var HOUR = 60 * MINUTE;
  var DAY = 24 * HOUR;

  function formatPostalCode(value) {
    var digits = (value || "").replace(/\D/g, "").slice(0, 7);
    if (digits.length <= 4) return digits;
    return digits.slice(0, 4) + "-" + digits.slice(4);
  }

  /** "3000-3049, 3500" -> [[3000, 3049], [3500, 3500]] */
  function parseRanges(raw) {
    return (raw || "")
      .split(",")
      .map(function (chunk) {
        var parts = chunk.trim().split("-");
        var from = parseInt(parts[0], 10);
        var to = parts.length > 1 ? parseInt(parts[1], 10) : from;
        if (isNaN(from) || isNaN(to)) return null;
        return [Math.min(from, to), Math.max(from, to)];
      })
      .filter(Boolean);
  }

  function matchesRanges(ranges, prefix) {
    return ranges.some(function (range) {
      return prefix >= range[0] && prefix <= range[1];
    });
  }

  /**
   * Next cutoff strictly in the future, and the first delivery weekday after it.
   * @returns {{ cutoff: Date, delivery: Date }}
   */
  function nextRunForRule(now, rule) {
    var cutoff = new Date(now);
    cutoff.setHours(rule.cutoffHour, rule.cutoffMinute, 0, 0);
    cutoff.setDate(cutoff.getDate() + ((rule.cutoffDay - cutoff.getDay() + 7) % 7));
    if (cutoff.getTime() <= now.getTime()) {
      cutoff.setDate(cutoff.getDate() + 7);
    }

    var delivery = new Date(cutoff);
    var gap = (rule.deliveryDay - cutoff.getDay() + 7) % 7;
    delivery.setDate(delivery.getDate() + (gap === 0 ? 7 : gap));
    delivery.setHours(0, 0, 0, 0);

    return { cutoff: cutoff, delivery: delivery };
  }

  /** Earliest delivery across all of a zone's rules. */
  function nextRunForZone(now, rules) {
    return rules
      .map(function (rule) {
        return nextRunForRule(now, rule);
      })
      .sort(function (a, b) {
        return a.delivery - b.delivery;
      })[0];
  }

  function capitalise(text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  function formatDate(date) {
    return capitalise(
      date.toLocaleDateString("pt-PT", {
        weekday: "long",
        day: "numeric",
        month: "long",
      })
    );
  }

  function formatCutoff(date) {
    var time = date.toLocaleTimeString("pt-PT", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return WEEKDAYS[date.getDay()] + " às " + time;
  }

  function plural(value, one, many) {
    return value + " " + (value === 1 ? one : many);
  }

  /** "2 dias e 14 horas" / "6 horas e 12 minutos" / "45 minutos" */
  function formatRemaining(ms) {
    if (ms <= 0) return "";

    var days = Math.floor(ms / DAY);
    var hours = Math.floor((ms % DAY) / HOUR);
    var minutes = Math.floor((ms % HOUR) / MINUTE);

    if (days > 0) {
      if (hours === 0) return plural(days, "dia", "dias");
      return plural(days, "dia", "dias") + " e " + plural(hours, "hora", "horas");
    }

    if (hours > 0) {
      if (minutes === 0) return plural(hours, "hora", "horas");
      return plural(hours, "hora", "horas") + " e " + plural(minutes, "minuto", "minutos");
    }

    return plural(Math.max(minutes, 1), "minuto", "minutos");
  }

  function readZone(card) {
    var rules;
    try {
      rules = JSON.parse(card.getAttribute("data-zone-rules") || "[]");
    } catch (error) {
      rules = [];
    }

    return {
      card: card,
      name: card.getAttribute("data-zone-name") || "",
      window: card.getAttribute("data-zone-window") || "",
      carrier: card.getAttribute("data-zone-carrier") || "",
      ranges: parseRanges(card.getAttribute("data-zone-ranges")),
      rules: rules.filter(function (rule) {
        return typeof rule.cutoffDay === "number" && typeof rule.deliveryDay === "number";
      }),
    };
  }

  function zoneSentence(zone, run) {
    var when = formatDate(run.delivery);
    if (zone.window) when += ", " + zone.window;
    return when;
  }

  function initSection(root) {
    if (!root || root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";

    var form = root.querySelector("[data-zone-form]");
    var input = root.querySelector("[data-zone-input]");
    var result = root.querySelector("[data-zone-result]");
    var waitlist = root.querySelector("[data-zone-waitlist]");
    var cards = Array.prototype.slice.call(root.querySelectorAll("[data-zone-card]"));
    var zones = cards.map(readZone);
    var activeZone = null;

    if (!zones.length) return;

    /** Fill every card with its real next delivery date, before any input. */
    function refreshCards() {
      var now = new Date();

      zones.forEach(function (zone) {
        if (!zone.rules.length) return;

        var run = nextRunForZone(now, zone.rules);
        var nextEl = zone.card.querySelector("[data-zone-next]");
        var cutoffEl = zone.card.querySelector("[data-zone-cutoff]");

        if (nextEl) nextEl.textContent = zoneSentence(zone, run);
        if (cutoffEl) {
          var remaining = formatRemaining(run.cutoff - now);
          cutoffEl.textContent = remaining
            ? "Encomende até " + formatCutoff(run.cutoff) + " (faltam " + remaining + ")"
            : "Encomende até " + formatCutoff(run.cutoff);
        }
      });

      if (activeZone) renderZoneResult(activeZone);
    }

    function clearHighlight() {
      zones.forEach(function (zone) {
        zone.card.classList.remove("is-active");
        zone.card.removeAttribute("data-zone-current");
      });
    }

    function setResult(type, html) {
      root.classList.add("has-result");
      result.className = "delivery-zones__result delivery-zones__result--" + type;
      result.innerHTML = html;
      result.hidden = false;
    }

    function showWaitlist(show) {
      if (!waitlist) return;
      waitlist.hidden = !show;
    }

    function renderZoneResult(zone) {
      var now = new Date();
      var run = nextRunForZone(now, zone.rules);
      var remaining = formatRemaining(run.cutoff - now);

      clearHighlight();
      zone.card.classList.add("is-active");
      zone.card.setAttribute("data-zone-current", "true");

      var html =
        "<strong>Sim, entregamos em " +
        zone.name +
        ".</strong>" +
        "<span class=\"delivery-zones__result-date\">Próxima entrega: <b>" +
        zoneSentence(zone, run) +
        "</b></span>" +
        "<span>Encomende até " +
        formatCutoff(run.cutoff) +
        (remaining ? " (faltam <b>" + remaining + "</b>)" : "") +
        ".</span>";

      if (zone.carrier) {
        html += '<span class="delivery-zones__result-carrier">' + zone.carrier + "</span>";
      }

      html +=
        '<a class="delivery-zones__result-cta" href="/pages/ementa">Ver a ementa desta semana</a>';

      setResult("success", html);
      showWaitlist(false);
    }

    function announceZone(zone, postalCode) {
      activeZone = zone;
      renderZoneResult(zone);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "delivery_check_success",
        location: "delivery_page_zone_checker",
        delivery_zone: zone.name,
        postal_code_prefix: postalCode.slice(0, 4),
      });
    }

    function announceUnavailable(postalCode) {
      activeZone = null;
      clearHighlight();
      setResult(
        "unavailable",
        "<strong>Ainda não chegamos aí.</strong>" +
          "<span>A entrega refrigerada está disponível em Portugal Continental. " +
          "Deixe o seu email e avisamos assim que chegarmos à sua zona.</span>"
      );
      showWaitlist(true);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "delivery_check_unavailable",
        location: "delivery_page_zone_checker",
        postal_code_prefix: postalCode.slice(0, 4),
      });
    }

    if (form && input && result) {
      input.addEventListener("input", function () {
        var formatted = formatPostalCode(input.value);
        if (input.value !== formatted) input.value = formatted;

        if (result.innerHTML) {
          activeZone = null;
          root.classList.remove("has-result");
          result.innerHTML = "";
          result.hidden = true;
          result.className = "delivery-zones__result";
          clearHighlight();
          showWaitlist(false);
        }
      });

      form.addEventListener("submit", function (event) {
        event.preventDefault();

        var postalCode = formatPostalCode(input.value);
        input.value = postalCode;

        if (!/^\d{4}-\d{3}$/.test(postalCode)) {
          setResult(
            "error",
            "<strong>Falta pouco.</strong><span>Escreva o código postal completo, no formato 0000-000.</span>"
          );
          input.focus();
          return;
        }

        var prefix = parseInt(postalCode.slice(0, 4), 10);

        if (prefix < 1000 || prefix > 9999) {
          setResult(
            "error",
            "<strong>Código postal inválido.</strong><span>Confirme os números e tente novamente.</span>"
          );
          input.focus();
          return;
        }

        var match = zones.filter(function (zone) {
          return zone.rules.length && matchesRanges(zone.ranges, prefix);
        })[0];

        if (!match) {
          announceUnavailable(postalCode);
          return;
        }

        announceZone(match, postalCode);
      });
    }

    refreshCards();
    setInterval(refreshCards, MINUTE);
  }

  function initAll() {
    document.querySelectorAll("[data-delivery-zones]").forEach(initSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", initAll);
})();
